import { useEffect, useState } from "react";
import { supabase } from "./supabase";
import { readCache, writeCache, cacheFresh } from "./useCachedQuery";

/**
 * Returns the total cents spent on scanned receipts within the given month.
 * Defaults to the current month in the user's local timezone.
 *
 *   { cents, receiptCount, loading }
 *
 * We re-query whenever `userId` or `refreshKey` changes — bump refreshKey
 * after saving a receipt to pull in the new total.
 */
// Monthly spend is recomputed once per hour at most. It changes
// only when a new receipt commits (Shop Mode checkout); the tally
// is otherwise static for the rest of the month. 1h TTL is
// generous — the user's spend pill won't update mid-trip without
// a refreshKey bump, but Shop Mode increments refreshKey itself
// to force a re-tally on commit.
const CACHE_KEY = (uid, ym) => `monthlySpend.v1:${uid}:${ym}`;
const TTL_MS    = 60 * 60 * 1000;

export function useMonthlySpend(userId, refreshKey = 0) {
  // Build the YYYY-MM string for the cache key; rolling over the
  // month invalidates last month's cache automatically.
  const now = new Date();
  const ymKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const cacheK = userId ? CACHE_KEY(userId, ymKey) : null;
  const cached = cacheK && refreshKey === 0 ? readCache(cacheK, TTL_MS) : null;

  const [state, setState] = useState(() => cached
    ? { ...cached, loading: false }
    : { cents: 0, receiptCount: 0, loading: !!userId },
  );

  useEffect(() => {
    let alive = true;
    if (!userId) {
      setState({ cents: 0, receiptCount: 0, loading: false });
      return;
    }
    // refreshKey > 0 forces a fresh fetch (Shop Mode commit nudge).
    if (refreshKey === 0 && cacheFresh(cacheK, TTL_MS)) {
      // Cache hit — already hydrated in initial state above.
      return;
    }

    // Month range in local time, formatted as YYYY-MM-DD strings since
    // receipt_date is a DATE column (no time component).
    const y = now.getFullYear();
    const m = now.getMonth();
    const pad = n => String(n).padStart(2, "0");
    const startStr = `${y}-${pad(m + 1)}-01`;
    // End of month, exclusive — gives us the first of next month.
    const endDate = new Date(y, m + 1, 1);
    const endStr = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-01`;

    (async () => {
      // Pull receipts for the current month. We prefer receipt_date when
      // set (the real transaction date), falling back to created_at for
      // receipts where the OCR couldn't read one.
      const { data, error } = await supabase
        .from("receipts")
        .select("total_cents, receipt_date, created_at")
        .eq("user_id", userId)
        .or(`and(receipt_date.gte.${startStr},receipt_date.lt.${endStr}),and(receipt_date.is.null,created_at.gte.${startStr},created_at.lt.${endStr})`);

      if (!alive) return;
      if (error) {
        console.warn("[useMonthlySpend] query failed:", error.message);
        setState({ cents: 0, receiptCount: 0, loading: false });
        return;
      }

      const cents = (data || []).reduce((sum, r) => sum + (r.total_cents || 0), 0);
      const next = { cents, receiptCount: (data || []).length };
      writeCache(cacheK, next);
      setState({ ...next, loading: false });
    })();

    return () => { alive = false; };
  }, [userId, refreshKey, cacheK]);

  return state;
}
